import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { mutate } from 'swr'
import type { Row } from '@tanstack/react-table'
import { CaretSortIcon, Pencil1Icon } from '@radix-ui/react-icons'
import { X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import Loader from '@/components/ui/Loader'
import { getFetcher } from 'api/swr'
import { cn } from '@/lib/utils'
import { CreateTagDialog } from 'components/create-tag-dialog'
import { PlusIcon } from '@radix-ui/react-icons'

const PAGE_SIZE = 100
const SEARCH_DEBOUNCE_MS = 1000

type TagLibItem = {
  id: string
  name: string
  note?: string
  level?: number
  frameCount?: number
}

const matchLoadedTag = (item: string | TagLibItem, query: string) => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  if (typeof item === 'string') return item.toLowerCase().includes(q)
  const name = String(item?.name || '').toLowerCase()
  const note = String(item?.note || '').toLowerCase()
  return name.includes(q) || note.includes(q)
}

type DraftLike = {
  id: string
  tags?: string[]
  ext?: {
    tags?: string[]
  }
}

type UpdateDraftHook = (id: string) => {
  handleUpdate: (
    key: string,
    value: string,
    revalidate?: boolean,
  ) => Promise<unknown> | unknown
  isMutating: boolean
}

type TagColumnMode = 'selected' | 'available'
type SelectionMode = 'multiple' | 'single'

type TagColumnProps = {
  title: string
  tags: Array<string | TagLibItem>
  selectedSet: Set<string>
  loading: boolean
  hasMore: boolean
  onLoadMore?: () => void
  onToggle: (name: string) => void
  mode: TagColumnMode
}

export const getInitialTags = (data: DraftLike): string[] => {
  if (Array.isArray(data?.ext?.tags)) return [...data.ext.tags]
  if (Array.isArray(data?.tags)) return [...data.tags]
  return []
}

/** 多选草稿的标签交集（用于批量编辑初始选中） */
export const getCommonTags = (items: DraftLike[]): string[] => {
  if (!items.length) return []
  const sets = items.map((item) => new Set(getInitialTags(item)))
  const [first, ...rest] = sets
  return [...first].filter((tag) => rest.every((set) => set.has(tag)))
}

const TagColumn = ({
  title,
  tags,
  selectedSet,
  loading,
  hasMore,
  onLoadMore,
  onToggle,
  mode,
}: TagColumnProps) => {
  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-md border">
      <div className="shrink-0 border-b px-3 py-2 text-sm font-medium">
        {title}
        <span className="ml-1 text-muted-foreground">({tags.length})</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-wrap content-start gap-1.5 overflow-y-auto p-3">
        {tags.length === 0 && !loading ? (
          <div className="w-full py-6 text-center text-sm text-muted-foreground">
            No tags
          </div>
        ) : (
          tags.map((item) => {
            const name = typeof item === 'string' ? item : item.name
            const note = typeof item === 'string' ? '' : item.note
            const selected = selectedSet.has(name)
            const disabled = mode === 'available' && selected
            return (
              <Badge
                key={name}
                variant={
                  mode === 'selected' || selected ? 'default' : 'outline'
                }
                className={cn(
                  'cursor-pointer',
                  disabled && 'pointer-events-none opacity-40',
                )}
                onClick={() => {
                  if (!disabled) onToggle(name)
                }}
              >
                <small>
                  {name}
                  {note ? `-${note}` : ''}
                  {typeof item !== 'string' && item.frameCount != null
                    ? ` (${item.frameCount})`
                    : ''}
                </small>
              </Badge>
            )
          })
        )}
        {loading && (
          <div className="flex w-full justify-center py-2">
            <Loader />
          </div>
        )}
      </div>
      {mode === 'available' && hasMore && (
        <div className="shrink-0 border-t p-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={loading}
            onClick={onLoadMore}
          >
            {loading ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}

const useTagPage = (level: 2 | 3, query: string) => {
  const [tags, setTags] = useState<TagLibItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)
  const afterRef = useRef<string | null>(null)
  const loadingRef = useRef(false)

  const fetchPage = useCallback(
    async ({ reset }: { reset: boolean }) => {
      if (loadingRef.current) return
      loadingRef.current = true
      setLoading(true)
      try {
        const cursor = reset ? undefined : afterRef.current || undefined
        const res = await getFetcher('/taglibs', {
          level,
          order: 'frameCount',
          limit: PAGE_SIZE,
          ...(cursor ? { after: cursor } : {}),
          ...(query ? { q: query } : {}),
        })
        const list = (Array.isArray(res) ? res : []) as TagLibItem[]
        setTags((prev) => (reset ? list : [...prev, ...list]))
        setHasMore(list.length >= PAGE_SIZE)
        afterRef.current = list.length > 0 ? list[list.length - 1].id : null
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [level, query],
  )

  const resetAndLoad = useCallback(async () => {
    afterRef.current = null
    setTags([])
    setHasMore(false)
    await fetchPage({ reset: true })
  }, [fetchPage])

  const loadMore = useCallback(async () => {
    if (!hasMore) return
    await fetchPage({ reset: false })
  }, [fetchPage, hasMore])

  return { tags, loading, hasMore, loadMore, resetAndLoad }
}

export type TagPickerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: React.ReactNode
  initialTags?: string[]
  selectionMode?: SelectionMode
  onConfirm: (tags: string[]) => Promise<void> | void
  trigger?: React.ReactNode
  busy?: boolean
  confirmLabel?: string
  allowCreate?: boolean
}

export const TagPickerDialog = ({
  open,
  onOpenChange,
  title = 'Edit Tags',
  initialTags = [],
  selectionMode = 'multiple',
  onConfirm,
  trigger,
  busy = false,
  confirmLabel = 'Confirm',
  allowCreate = true,
}: TagPickerDialogProps) => {
  const [selected, setSelected] = useState<string[]>(initialTags)
  const [submitting, setSubmitting] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const searchRef = useRef(search)
  searchRef.current = search
  const level2 = useTagPage(2, search)
  const level3 = useTagPage(3, search)

  useEffect(() => {
    if (!open) {
      setSearchInput('')
      setSearch('')
      return
    }
    const next =
      selectionMode === 'single'
        ? initialTags.slice(0, 1).filter(Boolean)
        : [...initialTags]
    setSelected(next)
    // only reset selection when the dialog opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const handle = setTimeout(() => {
      const next = searchInput.trim()
      if (next === searchRef.current) return
      setSearch(next)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [open, searchInput])

  useEffect(() => {
    if (!open) return
    void level2.resetAndLoad()
    void level3.resetAndLoad()
  }, [open, search, level2.resetAndLoad, level3.resetAndLoad])

  const selectedSet = useMemo(() => new Set(selected), [selected])

  const filteredSelected = useMemo(
    () => selected.filter((tag) => matchLoadedTag(tag, search)),
    [selected, search],
  )

  const addTag = (name: string) => {
    if (selectionMode === 'single') {
      setSelected([name])
      return
    }
    setSelected((prev) => (prev.includes(name) ? prev : [...prev, name]))
  }

  const removeTag = (name: string) => {
    setSelected((prev) => prev.filter((tag) => tag !== name))
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await onConfirm(selected)
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const isBusy = submitting || busy

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setCreateOpen(false)
        onOpenChange(next)
      }}
    >
      {trigger}
      <DialogContent className="flex max-h-[85vh] w-[min(1100px,95vw)] max-w-[1100px] flex-col gap-4">
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 gap-4 pr-8">
          <DialogTitle className="flex min-w-0 shrink items-center gap-2">
            {title}
            {selectionMode === 'single' ? (
              <span className="shrink-0 text-sm font-normal text-muted-foreground">
                (单选)
              </span>
            ) : null}
          </DialogTitle>
          <div className="flex shrink-0 items-center gap-2">
            <div className="relative w-56">
              <Input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search all tags"
                className="h-9"
              />
              {searchInput ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  title="Clear search"
                  onClick={() => {
                    setSearchInput('')
                    setSearch('')
                  }}
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
            {allowCreate ? (
              <Button
                type="button"
                variant="default"
                className="h-9"
                onClick={() => setCreateOpen(true)}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Create Tag
              </Button>
            ) : null}
          </div>
        </DialogHeader>

        <div
          className="grid min-h-0 flex-1 grid-cols-3 gap-3"
          style={{ height: '60vh' }}
        >
          <TagColumn
            title="Selected"
            tags={filteredSelected}
            selectedSet={selectedSet}
            loading={false}
            hasMore={false}
            mode="selected"
            onToggle={removeTag}
          />
          <TagColumn
            title="Level 2"
            tags={level2.tags}
            selectedSet={selectedSet}
            loading={level2.loading}
            hasMore={level2.hasMore}
            mode="available"
            onLoadMore={level2.loadMore}
            onToggle={addTag}
          />
          <TagColumn
            title="Level 3"
            tags={level3.tags}
            selectedSet={selectedSet}
            loading={level3.loading}
            hasMore={level3.hasMore}
            mode="available"
            onLoadMore={level3.loadMore}
            onToggle={addTag}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setCreateOpen(false)
              onOpenChange(false)
            }}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isBusy}
          >
            {isBusy ? 'Saving...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {allowCreate ? (
      <CreateTagDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={({ name }) => {
          addTag(name)
          void level2.resetAndLoad()
          void level3.resetAndLoad()
        }}
      />
    ) : null}
    </>
  )
}

/** 列表页 Tag 筛选：单选选择器，Confirm 后回调；Selected 清空再 Confirm = 清除筛选 */
export type ContentTagFilterProps = {
  value?: string | null
  onChange: (tag: string | null) => void
  id?: string
}

export const ContentTagFilter = ({
  value,
  onChange,
  id,
}: ContentTagFilterProps) => {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative w-[180px]">
      <TagPickerDialog
        open={open}
        onOpenChange={setOpen}
        title="Filter by Tag"
        initialTags={value ? [value] : []}
        selectionMode="single"
        confirmLabel="Apply"
        allowCreate={false}
        onConfirm={(selected) => {
          onChange(selected[0] || null)
        }}
        trigger={
          <DialogTrigger asChild>
            <button
              id={id}
              type="button"
              className={cn(
                'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background',
                'focus:outline-none focus:ring-1 focus:ring-ring',
                '[&>span]:line-clamp-1',
              )}
            >
              <span className="min-w-0 flex-1 truncate text-left font-normal">
                {value || 'All'}
              </span>
              <CaretSortIcon className="h-4 w-4 shrink-0 opacity-50" />
            </button>
          </DialogTrigger>
        }
      />
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          title="Clear tag"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onChange(null)
          }}
          className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}

type DataTableRowEditorTagsProps = {
  row: Row<DraftLike>
  useUpdateDrafts: UpdateDraftHook
}

const DataTableRowEditorTags = ({
  row,
  useUpdateDrafts,
}: DataTableRowEditorTagsProps) => {
  const data = row.original
  const [open, setOpen] = useState(false)
  const { handleUpdate, isMutating } = useUpdateDrafts(data.id)

  const handleConfirm = async (selected: string[]) => {
    const original = getInitialTags(data)
    const toAdd = selected.filter((tag) => !original.includes(tag))
    const toRemove = original.filter((tag) => !selected.includes(tag))
    if (toAdd.length === 0 && toRemove.length === 0) return

    for (const tag of toRemove) {
      await handleUpdate('removeTag', tag)
    }
    for (const tag of toAdd) {
      await handleUpdate('addTag', tag)
    }
    await mutate(
      (key) =>
        Array.isArray(key) &&
        (key[0] === '/drafts' || key[0] === '/dlc/drafts'),
    )
  }

  return (
    <TagPickerDialog
      open={open}
      onOpenChange={setOpen}
      title="Edit Tags"
      initialTags={getInitialTags(data)}
      selectionMode="multiple"
      onConfirm={handleConfirm}
      busy={isMutating}
      trigger={
        <DialogTrigger className="w-full">
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil1Icon className="mr-2 h-4 w-4" /> Edit Tags
          </DropdownMenuItem>
        </DialogTrigger>
      }
    />
  )
}

export default DataTableRowEditorTags
